import React from "react";
import { ArrowLeft, Shield, Lock, Eye, FileText, Users, Mail, Phone, MapPin } from "lucide-react";

const PrivacyPolicy = () => {
  const handleBackToHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFEBB5] to-[#FFF8E6]">
      {/* Header */}
      <div className="bg-[#67142D] text-white py-4">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <button
            onClick={handleBackToHome}
            className="flex items-center gap-2 hover:text-[#B51F3B] transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </button>
          <h1 className="text-2xl font-source-serif-4 font-semibold">Privacy Policy</h1>
          <div className="w-20"></div> {/* Spacer for centering */}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Header Info */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-source-serif-4 font-bold text-[#67142D] mb-4">
              Privacy Policy - Teerth Sewa Nyas
            </h1>
            <div className="flex justify-center gap-6 text-sm text-gray-600">
              <span>Effective Date: 01-03-2025</span>
              <span>•</span>
              <span>Last Updated: 24-03-2025</span>
            </div>
          </div>

          {/* Introduction */}
          <div className="mb-8">
            <p className="text-gray-700 leading-relaxed">
              Teerth Sewa Nyas ("we," "our," or "us") is committed to protecting your privacy. 
              This Privacy Policy describes how we collect, use, and safeguard your personal 
              information when you visit and interact with our website. By using our Website, 
              you consent to the practices described in this Privacy Policy.
            </p>
          </div>

          {/* Section 1: Information We Collect */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="text-[#67142D]" size={24} />
              <h2 className="text-2xl font-source-serif-4 font-semibold text-[#67142D]">
                1. Information We Collect
              </h2>
            </div>
            
            <div className="ml-7">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">1.1 Personal Information</h3>
              <p className="text-gray-700 mb-4">
                We collect personal information when you:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Register as a volunteer, donor, or member</li>
                <li>Make donations through our payment gateway</li>
                <li>Subscribe to our newsletters or updates</li>
                <li>Contact us through forms, emails, or phone calls</li>
                <li>Participate in our campaigns, events, or social initiatives</li>
              </ul>
              
              <p className="text-gray-700 mb-2">Personal information may include:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li>Full Name</li>
                <li>Email Address</li>
                <li>Phone Number</li>
                <li>Address (City, State, Country)</li>
                <li>Payment Information (processed through third-party payment gateways)</li>
                <li>Any other information you voluntarily provide</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">1.2 Non-Personal Information</h3>
              <p className="text-gray-700 mb-2">We may also collect non-personal data automatically, such as:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Browser type and version</li>
                <li>IP address</li>
                <li>Operating system</li>
                <li>Referring website</li>
                <li>Pages visited, duration, and activity on the Website</li>
                <li>Cookies and tracking technologies</li>
              </ul>
            </div>
          </div>

          {/* Section 2: How We Use Your Information */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Users className="text-[#67142D]" size={24} />
              <h2 className="text-2xl font-source-serif-4 font-semibold text-[#67142D]">
                2. How We Use Your Information
              </h2>
            </div>
            
            <div className="ml-7">
              <p className="text-gray-700 mb-2">We use the collected data for:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Providing and managing our services, including donation processing</li>
                <li>Sending receipts, confirmations, and transaction details</li>
                <li>Communicating updates, newsletters, and promotional materials</li>
                <li>Enhancing user experience and improving website performance</li>
                <li>Ensuring security, fraud prevention, and compliance with legal obligations</li>
                <li>Responding to inquiries, support requests, and feedback</li>
              </ul>
            </div>
          </div>

          {/* Section 3: Cookies and Tracking */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Eye className="text-[#67142D]" size={24} />
              <h2 className="text-2xl font-source-serif-4 font-semibold text-[#67142D]">
                3. Cookies and Tracking Technologies
              </h2>
            </div>
            
            <div className="ml-7">
              <p className="text-gray-700 mb-2">Our Website uses cookies and tracking technologies to:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li>Enhance user experience by remembering preferences</li>
                <li>Analyze website traffic and performance</li>
                <li>Ensure secure transactions and authentication</li>
              </ul>
              <p className="text-gray-700">
                You can manage or disable cookies through your browser settings. However, 
                disabling cookies may impact certain Website functionalities.
              </p>
            </div>
          </div>

          {/* Section 4: Payment Processing and Security */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="text-[#67142D]" size={24} />
              <h2 className="text-2xl font-source-serif-4 font-semibold text-[#67142D]">
                4. Payment Processing and Security
              </h2>
            </div>
            
            <div className="ml-7">
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>All online donations and payments are securely processed through third-party payment gateways</li>
                <li>We do not store or process your credit/debit card details on our servers</li>
                <li>Payment gateways comply with PCI-DSS (Payment Card Industry Data Security Standard) to ensure data security</li>
                <li>We use SSL (Secure Socket Layer) encryption to protect sensitive data during transactions</li>
              </ul>
              <p className="text-gray-700 mt-4">
                If you encounter any unauthorized transaction, please contact us immediately.
              </p>
            </div>
          </div>

          {/* Section 5: Data Sharing */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="text-[#67142D]" size={24} />
              <h2 className="text-2xl font-source-serif-4 font-semibold text-[#67142D]">
                5. Data Sharing and Third-Party Services
              </h2>
            </div>
            
            <div className="ml-7">
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>We do NOT sell, rent, or trade your personal information to third parties</li>
                <li>We may share your data with trusted third-party service providers for:
                  <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                    <li>Payment processing</li>
                    <li>Email communications</li>
                    <li>Website hosting and maintenance</li>
                    <li>Analytics and performance tracking</li>
                  </ul>
                </li>
                <li>Third parties have limited access to your data and are bound by confidentiality agreements</li>
              </ul>
            </div>
          </div>

          {/* Section 6: Your Rights */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-semibold text-[#67142D] mb-4">
              6. Your Rights and Choices
            </h2>
            
            <div className="ml-7">
              <p className="text-gray-700 mb-2">You have the following rights regarding your personal data:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Access & Correction: Request a copy of your stored data and correct inaccuracies</li>
                <li>Data Deletion: Request removal of your personal information</li>
                <li>Opt-Out: Unsubscribe from emails, newsletters, and marketing communications</li>
                <li>Withdraw Consent: Change or withdraw consent for data usage at any time</li>
              </ul>
            </div>
          </div>

          {/* Section 7: Children's Privacy */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-semibold text-[#67142D] mb-4">
              7. Children's Privacy
            </h2>
            
            <div className="ml-7">
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Our Website is not intended for children under 13 years of age</li>
                <li>We do not knowingly collect personal data from minors</li>
                <li>If a parent or guardian believes we have collected a child's data, please contact us for immediate deletion</li>
              </ul>
            </div>
          </div>

          {/* Section 8: Compliance */}
          <div className="mb-8">
            <h2 className="text-2xl font-source-serif-4 font-semibold text-[#67142D] mb-4">
              8. Compliance with Legal Obligations
            </h2>
            
            <div className="ml-7">
              <p className="text-gray-700 mb-2">We comply with applicable laws and regulations related to data protection, including:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>India's IT Act (2000) & IT Rules (2011)</li>
                <li>General Data Protection Regulation (GDPR) (if applicable)</li>
                <li>Payment Security Standards (PCI-DSS)</li>
              </ul>
              <p className="text-gray-700 mt-4">
                We may disclose data if required by law, court orders, or government authorities.
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-[#F8F9FA] rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-source-serif-4 font-semibold text-[#67142D] mb-4">
              Contact Us
            </h2>
            
            <p className="text-gray-700 mb-4">
              For any questions or concerns about this Privacy Policy, you can contact us at:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <MapPin className="text-[#67142D] mt-1" size={20} />
                <div>
                  <p className="font-semibold text-gray-800">Registered Office</p>
                  <p className="text-gray-700 text-sm">
                    B-11- अलंकार पैलेस, नज़दीक शंकर आश्रम,<br />
                    ज्वालापुर, हरिद्वार, उत्तराखंड - 249407
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Phone className="text-[#67142D] mt-1" size={20} />
                <div>
                  <p className="font-semibold text-gray-800">Phone Numbers</p>
                  <p className="text-gray-700 text-sm">
                    9520050068 / 9520050038 / 9520050083
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Mail className="text-[#67142D] mt-1" size={20} />
                <div>
                  <p className="font-semibold text-gray-800">Email</p>
                  <p className="text-gray-700 text-sm">
                    info@teerthsewanyas.com
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Note */}
          <div className="text-center text-sm text-gray-600">
            <p>
              This Privacy Policy was last updated on March 24, 2025. 
              We reserve the right to update this policy at any time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
